import { ReactComponent as OutlineUpload } from "assets/svg/outline-upload.svg";
import { ReactComponent as OutlineUpload24 } from "assets/svg/outline-upload-24.svg";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as styles from "./RecordingUploadZone.module.scss";
// import { ReactComponent as SoundIcon } from "assets/svg/sound-icon.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlineTrash } from "assets/svg/outline-trash-1.svg";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import ConfirmModal from "components/confirm-modal";
import { AUDIO_RECORDING_S3_BUCKET, AWSCredentials } from "constants/aws";
import useRecordingUpload from "hooks/useRecordingUpload";
import { Button, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import queryS3DownloadUrl from "services/queryS3DownloadUrl";
import deleteRecording from "services/transcription/deleteRecording";
import queryRecordingS3Key from "services/transcription/queryRecordingS3Key";
import queryTranscriptionBucketTempCredentials from "services/transcription/queryTranscriptionBucketTempCredentials";
import { AmplitudePageName, track } from "utils/amplitude";

import IconButton from "components/icon-button";
import getClasses from "utils/getClasses";
import SingleBarChart from "components/single-bar-chart/SingleBarChart";
import { appointmentHasRecordingInProgress } from "utils/recordingStatusUtils";
import postSessionRecordingMetadata from "services/transcription/postSessionRecordingMetadata";

const AMPLITUDE_COMPONENT_NAME = "appointment-audio-upload";

const S3_BUCKET = AUDIO_RECORDING_S3_BUCKET;
const S3_KEY_QUERY_PARAM = "s3-key";
const FILE_NAME_QUERY_PARAM = "file-name";
const FILE_SIZE_QUERY_PARAM = "file-size";

interface Props {
  onUploadComplete: (
    s3Key: string,
    fileName: string,
    fileSize: string,
    preUploaded?: boolean,
  ) => void;
  setError: React.Dispatch<React.SetStateAction<string | Error | undefined>>;
  amplitudePageName: AmplitudePageName;
  onReset?: () => void;
  onReupload?: () => void;
  reuploadWarning?: string;
  uploadProcessingMessage?: string;
  doConfirmReupload: boolean;
  fileDeletionWarning?: string;
  appointmentId?: string;
  preventDeletion: boolean;
  isInScheduleModal?: boolean;
  onAudioPlayerTimeUpdate?: (timeMillis: number) => void;
  audioPlayerRef?: React.RefObject<HTMLAudioElement>;
  appointment?: AppointmentMetaData;
  recordingSourceUnavailable?: boolean;
}

const RecordingUploadZone = ({
  onUploadComplete,
  setError,
  amplitudePageName,
  onReset,
  onReupload,
  reuploadWarning,
  uploadProcessingMessage,
  doConfirmReupload,
  fileDeletionWarning,
  appointmentId,
  preventDeletion,
  isInScheduleModal,
  onAudioPlayerTimeUpdate,
  audioPlayerRef,
  appointment,
  recordingSourceUnavailable,
}: Props) => {
  type UploadState = "pre-upload" | "uploading" | "uploaded";
  const [uploadState, setUploadState] = useState<UploadState>("pre-upload");
  const {
    uploadFile,
    uploadComplete,
    uploadProgress,
    s3Key,
    reset: resetRecordingUpload,
    fileName,
    fileSize,
    error: recordingUploadError,
  } = useRecordingUpload();
  const [urlFileName, setUrlFileName] = useState<string | null>(
    "Session Audio File",
  );
  const [urlFileSize, setUrlFileSize] = useState<string | null>(null);
  const [fileUploadEvent, setFileUploadEvent] = useState<Event | null>(null);
  const [savedDataTransferFile, setSavedDataTransferFile] = useState<
    any | null
  >(null);
  const [showReuploadConfirm, setShowReuploadConfirm] =
    useState<boolean>(false);
  const [draggingOver, setDraggingOver] = useState<boolean>(false);
  const [showConfirmDeleteUpload, setShowConfirmDeleteUpload] =
    useState<boolean>(false);
  const [showPreventDeletion, setShowPreventDeletion] =
    useState<boolean>(false);
  const [urlFileDeleted, setUrlFileDeleted] = useState<boolean>(false);
  const [s3KeyOfPreviousUpload, setS3KeyOfPreviousUpload] = useState<
    string | null
  >(null);
  const [uploadedFileSignedUrl, setUploadedFileSignedUrl] =
    useState<string>("");
  const navigate = useNavigate();

  const isFileUploaded = useMemo(
    () => !recordingSourceUnavailable && (!!s3Key || !!s3KeyOfPreviousUpload),
    [recordingSourceUnavailable, s3Key, s3KeyOfPreviousUpload],
  );

  useEffect(() => {
    if (!isFileUploaded || !appointmentId) {
      return;
    }

    queryS3DownloadUrl(appointmentId)
      .then((url) => setUploadedFileSignedUrl(url ?? ""))
      .catch(() => setUploadedFileSignedUrl(""));
  }, [isFileUploaded, appointmentId]);

  useEffect(() => {
    const checkForExistingUpload = async () => {
      if (appointmentId) {
        try {
          const { s3_key: s3KeyFromResponse } = await queryRecordingS3Key(
            appointmentId,
          );
          if (s3KeyFromResponse) {
            setS3KeyOfPreviousUpload(s3KeyFromResponse);
            return;
          }
        } catch (e) {
          console.warn(e);
        }
      }

      const query = new URLSearchParams(window.location.search);
      if (query.get(S3_KEY_QUERY_PARAM)) {
        setS3KeyOfPreviousUpload(query.get(S3_KEY_QUERY_PARAM));
      }
    };
    checkForExistingUpload();
  }, [setS3KeyOfPreviousUpload, appointmentId]);

  // After upload to S3 completes, start the transcription job
  useEffect(() => {
    // If a previous s3 key exists, we initialize this component
    // to "already" have an uploaded recording
    if (
      !recordingSourceUnavailable &&
      s3KeyOfPreviousUpload &&
      uploadState !== "uploaded" &&
      !urlFileDeleted
    ) {
      setUploadState("uploaded");
      onUploadComplete(s3KeyOfPreviousUpload, fileName, fileSize, true);
    } else if (uploadComplete && s3Key && uploadState === "uploading") {
      setUploadState("uploaded");
      onUploadComplete(s3Key, fileName, fileSize, false);
    }
  }, [
    s3Key,
    s3KeyOfPreviousUpload,
    uploadComplete,
    uploadState,
    setUploadState,
    onUploadComplete,
    fileName,
    fileSize,
    urlFileDeleted,
    recordingSourceUnavailable,
  ]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get(FILE_NAME_QUERY_PARAM)) {
      setUrlFileName(query.get(FILE_NAME_QUERY_PARAM));
    }
    if (query.get(FILE_SIZE_QUERY_PARAM)) {
      setUrlFileSize(query.get(FILE_SIZE_QUERY_PARAM));
    }
  }, [setUrlFileName, setUrlFileSize]);

  const resetUploader = () => {
    resetRecordingUpload();
    setUploadState("pre-upload");
    setError(undefined);
    setShowConfirmDeleteUpload(false);
    setShowPreventDeletion(false);
  };

  const onUpload = async (e) => {
    if (doConfirmReupload) {
      setFileUploadEvent(e);
      setShowReuploadConfirm(true);
      return;
    } else {
      upload(e);
    }
  };

  const upload = async (e?) => {
    const event = e || fileUploadEvent;
    const awsCredentialsJSON = await queryTranscriptionBucketTempCredentials();
    const awsCredentials: AWSCredentials = {
      accessKeyId: awsCredentialsJSON.access_key_id,
      secretAccessKey: awsCredentialsJSON.secret_access_key,
      sessionToken: awsCredentialsJSON.session_token,
    };
    uploadFile(event.target.files[0], S3_BUCKET, awsCredentials, appointmentId);
    setError(undefined);
    setUploadState("uploading");
  };

  const downloadFile = async (url: string) => {
    try {
      /**
       * Needs more work, but this is the gist of the code to get download to work without opening a new tab
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "include",
      });

      const blob = await response.blob();

      if (!response.ok) {
        setError("Failed to download audio recording");
        return;
      }

      // Create a temporary link and trigger the download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      //link.download = "session-audio.wav"; // Set the desired file name
      document.body.appendChild(link);
      link.addEventListener("load", () => {
        document.body.removeChild(link);
      });
      link.click();
      */
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const onDrop = async (e) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault();

    if (doConfirmReupload) {
      setSavedDataTransferFile(e.dataTransfer.items[0].getAsFile());
      setShowReuploadConfirm(true);
    } else {
      uploadDroppedFile(e.dataTransfer.items[0].getAsFile());
    }
  };

  function removeQueryParam(paramName: string) {
    // Create a URLSearchParams object from the current URL
    const urlSearchParams = new URLSearchParams(window.location.search);

    // Remove the specified query parameter
    urlSearchParams.delete(paramName);

    // Get the updated query string
    const updatedQueryString = urlSearchParams.toString();

    // Create the updated URL without the specified query parameter
    const updatedUrl = `${window.location.pathname}?${updatedQueryString}`;

    // Replace the current URL in the browser's history
    navigate(updatedUrl, { replace: true });
  }

  const uploadDroppedFile = async (file: File) => {
    setUploadState("uploading");
    setError(undefined);

    const awsCredentialsJSON = await queryTranscriptionBucketTempCredentials();
    const awsCredentials: AWSCredentials = {
      accessKeyId: awsCredentialsJSON.access_key_id,
      secretAccessKey: awsCredentialsJSON.secret_access_key,
      sessionToken: awsCredentialsJSON.session_token,
    };
    uploadFile(file, S3_BUCKET, awsCredentials, appointmentId);
  };

  useEffect(() => {
    if (recordingUploadError) {
      setUploadState("pre-upload");
      setError(recordingUploadError);
    }
  }, [recordingUploadError, setError]);

  useEffect(() => {
    if (recordingSourceUnavailable) {
      setUploadedFileSignedUrl("");
      setUploadState("pre-upload");
    }
  }, [recordingSourceUnavailable]);

  const onCanPlay = useCallback(async () => {
    if (!appointment) {
      return;
    }

    if (
      !appointmentHasRecordingInProgress(appointment) ||
      !appointment.session_recording_s3_key
    ) {
      return;
    }

    // Recording already exists for the appointment, but the metadata says it's still in progress
    // We need to update the metadata to say it's finished

    await postSessionRecordingMetadata({
      appointmentId: appointment.appointment_id,
      s3Key: appointment.session_recording_s3_key,
      orchidProLeft: true,
      uploadMetadata: {
        finished: true,
      },
    });
  }, [appointment]);

  let content;
  if (uploadState === "pre-upload") {
    content = (
      <div
        className={getClasses(
          isInScheduleModal && styles["preupload-dropzone--in-modal"],
          styles["preupload-dropzone"],
        )}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDraggingOver(true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDraggingOver(false);
          onDrop(e);
          track(
            amplitudePageName,
            [AMPLITUDE_COMPONENT_NAME, "upload-zone"],
            "drop",
          );
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDraggingOver(false);
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {draggingOver ? (
          <div
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{ pointerEvents: "none" }}
          >
            <div
              className={styles["upload-drop-content"]}
              style={{ pointerEvents: "none" }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <OutlineUpload />
              <div
                className={getClasses(
                  // !urlFileDeleted && "mt-3",
                  styles["upload-drop-text"],
                )}
              >
                Drop here to instantly upload your file
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div
              className={getClasses(
                // !urlFileDeleted && "mb-3",
                styles["dropzone-title"],
              )}
            >
              Upload your session recording
            </div>
            <div className={styles["dropzone-instructions"]}>
              Drop a session file here or{" "}
              <Button variant={"link"} className={styles["upload-button"]}>
                <label className={styles["upload-label"]}>
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={onUpload}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        [AMPLITUDE_COMPONENT_NAME, "upload-button"],
                        "click",
                      )
                    }
                  />
                  Click to browse.
                </label>
              </Button>
            </div>
            {/* {!urlFileDeleted && (
              <div className={styles["dropzone-file-instructions"]}>
                .mp4 / .m4a / .mp3 / .ogg / .webm / .wav / .mov /
                <br />
                1-90 min long. Max. 150MB
              </div>
            )} */}
          </div>
        )}
      </div>
    );
  } else if (uploadState === "uploading") {
    content = (
      <div
        className={getClasses(
          isInScheduleModal && styles["uploading-dropzone--in-modal"],
          styles["uploading-dropzone"],
        )}
      >
        <div className={styles["uploading-dropzone-content"]}>
          <div className={styles["uploading-dropzone-text"]}>
            {fileName || urlFileName}
          </div>
          {isInScheduleModal && (
            <div className={styles["uploading-dropzone-file-size"]}>
              {fileSize || urlFileSize}
            </div>
          )}
          <div className={styles["uploading-dropzone-progress-bar"]}>
            <SingleBarChart percentValue={uploadProgress} trackColor="white" />
            <span>{uploadProgress}%</span>
          </div>
        </div>
      </div>
    );
  } else {
    content = isInScheduleModal ? (
      <div className={styles["uploaded-file-container"]}>
        <IconButton
          className={styles["delete-audio-file-button"]}
          onClick={() => {
            track(
              amplitudePageName,
              [AMPLITUDE_COMPONENT_NAME, "delete-audio-file-button"],
              "click",
            );
            if (preventDeletion) {
              setShowPreventDeletion(true);
            } else {
              setShowConfirmDeleteUpload(true);
            }
          }}
        >
          <OutlineCross />
        </IconButton>
        <div className={styles["uploaded-file-name-1"]}>
          {fileName || urlFileName}
        </div>
        <div className={styles["uploaded-file-size-1"]}>
          {fileSize || urlFileSize}
        </div>
        <div className={styles["uploaded-success"]}>
          <OutlineUpload24 /> File successfully uploaded
        </div>
      </div>
    ) : (
      <div className={styles["uploaded-file-wrapper"]}>
        <div className={styles["uploaded-file-name-and-button"]}>
          <div className={styles["uploaded-file-text"]}>
            <div className={styles["uploaded-file-name"]}>
              {fileName || urlFileName}
            </div>
            <div className={styles["uploaded-file-size"]}>
              {fileSize || urlFileSize}
            </div>
          </div>
          <ActionsDropdown drop={"down"}>
            {uploadedFileSignedUrl && (
              <Dropdown.Item
                className={styles["uploaded-file-download-button"]}
                onClick={async () => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "download-audio-file-button"],
                    "click",
                  );
                  downloadFile(uploadedFileSignedUrl);
                }}
              >
                <OutlineDownload /> Download Recording
              </Dropdown.Item>
            )}

            <Dropdown.Item
              onClick={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "delete-audio-file-button"],
                  "click",
                );
                if (preventDeletion) {
                  setShowPreventDeletion(true);
                } else {
                  setShowConfirmDeleteUpload(true);
                }
              }}
            >
              <OutlineTrash /> Delete Recording
            </Dropdown.Item>
          </ActionsDropdown>
        </div>

        <div className={styles["uploaded-file-tile"]}>
          {/* <SoundIcon className={styles["sound-icon"]} /> */}
          <audio
            ref={audioPlayerRef}
            src={uploadedFileSignedUrl}
            controls
            controlsList="noplaybackrate nodownload"
            onTimeUpdate={(event) => {
              if (onAudioPlayerTimeUpdate) {
                onAudioPlayerTimeUpdate(
                  (event.target as HTMLAudioElement).currentTime * 1000,
                );
              }
            }}
            onCanPlay={onCanPlay}
          />
        </div>

        {/* {uploadProcessingMessage && (
          <div className={styles["uploaded-file-summary-message"]}>
            {uploadProcessingMessage}
          </div>
        )} */}
      </div>
    );
  }

  return (
    <>
      {content}
      {showReuploadConfirm && (
        <ConfirmModal
          show={showReuploadConfirm}
          onConfirm={() => {
            if (fileUploadEvent) {
              onReupload && onReupload();
              setShowReuploadConfirm(false);
              upload(fileUploadEvent);
            } else if (savedDataTransferFile) {
              onReupload && onReupload();
              setShowReuploadConfirm(false);
              uploadDroppedFile(savedDataTransferFile);
            }
          }}
          onCancel={() => setShowReuploadConfirm(false)}
          labelTitle={"Are you sure?"}
          labelCancel={"Nevermind"}
          labelConfirm={"Yes, Add a new file"}
        >
          <div>
            {reuploadWarning ||
              "If you add a new file, your previous upload will no longer be available."}
          </div>
        </ConfirmModal>
      )}
      {showPreventDeletion && (
        <ConfirmModal
          show={showPreventDeletion}
          onConfirm={async () => {
            track(
              amplitudePageName,
              [
                AMPLITUDE_COMPONENT_NAME,
                "acknowledge-cant-yet-delete-audio-file-button",
              ],
              "click",
            );
            setShowPreventDeletion(false);
          }}
          onCancel={async () => {
            track(
              amplitudePageName,
              [
                AMPLITUDE_COMPONENT_NAME,
                "acknowledge-cant-yet-delete-audio-file-button",
              ],
              "click",
            );
            setShowPreventDeletion(false);
          }}
          hideCancel
          labelTitle={"Cannot Delete Audio File"}
          labelCancel={"Ok"}
          labelConfirm={"Ok"}
        >
          <div>
            You cannot delete the audio file until it is finished processing.
            Please try again in a few minutes.
          </div>
        </ConfirmModal>
      )}
      {showConfirmDeleteUpload && (
        <ConfirmModal
          show={showConfirmDeleteUpload}
          onConfirm={async () => {
            track(
              amplitudePageName,
              [AMPLITUDE_COMPONENT_NAME, "confirm-delete-audio-file-button"],
              "click",
            );

            /**
             * We must delete any existing recording first, because uploading a recording
             * will overwrite the reference to the old recording's s3 key, which would make it impossible
             * for a pro to delete later on
             */
            if (appointmentId) {
              await deleteRecording(appointmentId);
            }

            removeQueryParam(S3_KEY_QUERY_PARAM);
            removeQueryParam(FILE_NAME_QUERY_PARAM);
            removeQueryParam(FILE_SIZE_QUERY_PARAM);

            onReset && onReset();
            setUrlFileDeleted(true);
            resetUploader();
          }}
          onCancel={() => {
            track(
              amplitudePageName,
              [AMPLITUDE_COMPONENT_NAME, "cancel-delete-audio-file-button"],
              "click",
            );
            setShowConfirmDeleteUpload(false);
          }}
          labelTitle={"Are you sure?"}
          labelCancel={"Nevermind"}
          labelConfirm={"Yes, Delete"}
        >
          <div>
            {fileDeletionWarning ||
              "If you delete this file, it will no longer be available."}
          </div>
        </ConfirmModal>
      )}
    </>
  );
};

export default RecordingUploadZone;
